import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { IconRefresh, IconPencil, IconTrash } from '@tabler/icons-react';
import type { ReconciliationVariance, VarianceType } from '@techgitter/contracts';
import { fetchVariances, reopenVariance } from '../../services/reconciliationApi';
import { DeferDialog } from './DeferDialog';
import { textMatches } from './reconciliationFilter';
import { ReconciliationFilterFields } from './ReconciliationFilterFields';
import {
  SecondaryToolbar,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX } from '../../styles/designSystem';

const VARIANCE_TYPE_LABEL: Record<VarianceType, string> = {
  seat: 'Seat',
  term: 'Term',
  coverage: 'Coverage',
  renewalSeat: 'Renewal: Seats',
  renewalTerm: 'Renewal: Term',
  renewalPlan: 'Renewal: Plan',
};

/** Local (Australia/Sydney) date as YYYY-MM-DD — the deferral cutoff. */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/**
 * Deferred breaks: variances resolved with an Action Date on/after today. The
 * engine suppresses the matching break (keeps this explanation) until the date,
 * then reopens it if still unresolved. Admins can reschedule (edit the action
 * date) or cancel a deferral (Delete → reopen the break to the worklist).
 */
interface Props {
  customerFilter?: string;
  productFilter?: string;
  onCustomerFilterChange?: (v: string) => void;
  onProductFilterChange?: (v: string) => void;
  /** Admin — enables editing the action date and cancelling (reopening) a deferral. */
  canResolve?: boolean;
}

export const DeferredPanel: React.FC<Props> = ({
  customerFilter = '',
  productFilter = '',
  onCustomerFilterChange,
  onProductFilterChange,
  canResolve = false,
}) => {
  const [rows, setRows] = useState<ReconciliationVariance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ReconciliationVariance | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReconciliationVariance | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const items = await fetchVariances(
        { status: 'resolved', actionDateFrom: todayLocal() },
        signal,
      );
      if (!signal?.aborted) setRows(items);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load deferred breaks');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const sorted = useMemo(
    () =>
      rows
        .filter(
          (v) =>
            textMatches(customerFilter, v.customerName, v.customerId) &&
            textMatches(productFilter, v.vendorProductName, v.vendorProductSku),
        )
        .sort((a, b) => (a.actionDate ?? '').localeCompare(b.actionDate ?? '')),
    [rows, customerFilter, productFilter],
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await reopenVariance(deleteTarget.id);
      setDeleteTarget(null);
      setToast('Deferral cancelled — break reopened');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel deferral');
    } finally {
      setDeleting(false);
    }
  };

  const colSpan = canResolve ? 8 : 7;

  return (
    <>
      <SecondaryToolbar floating>
        <ReconciliationFilterFields
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={onCustomerFilterChange ?? (() => {})}
          onProductFilterChange={onProductFilterChange ?? (() => {})}
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
          Breaks resolved with a future Action Date — suppressed until then, then they
          reopen if still unresolved.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Action date</DataTableHeadCell>
                  <DataTableHeadCell>Type</DataTableHeadCell>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>Vendor product</DataTableHeadCell>
                  <DataTableHeadCell align="right">Expected</DataTableHeadCell>
                  <DataTableHeadCell align="right">Vendor reported</DataTableHeadCell>
                  <DataTableHeadCell>Resolution</DataTableHeadCell>
                  {canResolve && <DataTableHeadCell align="right">Action</DataTableHeadCell>}
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <DataTableEmpty colSpan={colSpan}>No deferred breaks.</DataTableEmpty>
                ) : (
                  sorted.map((v) => (
                    <DataTableRow key={v.id}>
                      <DataTableCell>{fmtDate(v.actionDate)}</DataTableCell>
                      <DataTableCell>
                        <Chip>{VARIANCE_TYPE_LABEL[v.varianceType]}</Chip>
                      </DataTableCell>
                      <DataTableCell>{v.customerName ?? v.customerId ?? '—'}</DataTableCell>
                      <DataTableCell>
                        {v.vendorProductName ?? v.vendorProductSku ?? '—'}
                      </DataTableCell>
                      <DataTableCell align="right">{v.expectedValue ?? '—'}</DataTableCell>
                      <DataTableCell align="right">{v.actualValue ?? '—'}</DataTableCell>
                      <DataTableCell muted>
                        <span title={v.resolutionNotes ?? undefined}>
                          {v.resolutionType ?? 'resolved'}
                        </span>
                      </DataTableCell>
                      {canResolve && (
                        <DataTableCell align="right">
                          <IconButton
                            size="small"
                            title="Edit deferral"
                            onClick={() => setEditTarget(v)}
                          >
                            <IconPencil size={16} stroke={1.5} />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Cancel deferral (reopen break)"
                            onClick={() => setDeleteTarget(v)}
                          >
                            <IconTrash size={16} stroke={1.5} />
                          </IconButton>
                        </DataTableCell>
                      )}
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </Box>
        )}
      </Box>

      <DeferDialog
        open={!!editTarget}
        variance={editTarget}
        mode="edit"
        onClose={() => setEditTarget(null)}
        onDeferred={() => {
          setEditTarget(null);
          setToast('Deferral updated');
          load();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Cancel deferral?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancel the deferral
            {deleteTarget
              ? ` for "${deleteTarget.customerName ?? deleteTarget.customerId}"`
              : ''}
            ? The break reopens to the active worklist (its resolution and action date are
            cleared). You can defer it again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Keep deferred
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Cancelling…' : 'Cancel deferral'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};
